import { createContext, type FC, type ReactNode, useContext } from 'react';

export type ServerVarsContextType = {
    isBot?: boolean; // should be set only for SSR
    isDesktop?: boolean | null;
    isMobile?: boolean | null;
    isSbAdsEnabled?: boolean | null;
    isTablet?: boolean | null;
    clientConf?: { [key: string]: string | number | undefined } | null;
    localeOrigin?: string | null;
    isSearchQueryApplied?: boolean | null;
    locale?: string | null;
    originalUrl?: string | null;
    isSoldPDPV3Variant?: boolean | null;
    isTradeQuickFiltersVariant?: boolean;
    isPdpMwLayoutShiftFixVariant?: boolean;
    isItemLocationFilterVariant?: boolean;
    isTableSizeFilterVariant?: boolean;
    urgencySignalsMwVariant?: string;
    isContentVisibilitySbVariant?: boolean;
    isPricingGuidanceCapVariant?: boolean;
    isPdpRespPreviewPage?: boolean;
    isMobileFilterMenuUxVariant?: boolean;
    isBannerAdsAbTestVariant?: boolean;
    isShipsFromSBTileVariant?: boolean;
};

export const ServerVarsContext = createContext<ServerVarsContextType>({
    isBot: false,
    isDesktop: null,
    isMobile: null,
    isSbAdsEnabled: null,
    isTablet: null,
    clientConf: {},
    localeOrigin: null,
    locale: null,
    originalUrl: null,
    isSearchQueryApplied: null,
    isTradeQuickFiltersVariant: false,
    isPdpMwLayoutShiftFixVariant: false,
    isItemLocationFilterVariant: false,
    isTableSizeFilterVariant: false,
    urgencySignalsMwVariant: undefined, // if test is off, "urgencySignalsMwVariant" should not have value
    isContentVisibilitySbVariant: false,
    isPricingGuidanceCapVariant: false,
    isPdpRespPreviewPage: false,
    isMobileFilterMenuUxVariant: false,
    isBannerAdsAbTestVariant: false,
    isShipsFromSBTileVariant: false,
});

export const useServerVarsContext = (): ServerVarsContextType =>
    useContext<ServerVarsContextType>(ServerVarsContext);

export const ServerVarsContextConsumer = ServerVarsContext.Consumer;

export const ServerVarsContextProvider: FC<ServerVarsContextType & { children: ReactNode }> = ({
    children,
    isDesktop,
    isMobile,
    isSbAdsEnabled,
    isTablet,
    clientConf,
    localeOrigin,
    originalUrl,
    isSearchQueryApplied,
    locale,
    isSoldPDPV3Variant,
    isBot,
    isTradeQuickFiltersVariant,
    isPdpMwLayoutShiftFixVariant,
    isItemLocationFilterVariant,
    isTableSizeFilterVariant,
    urgencySignalsMwVariant,
    isContentVisibilitySbVariant,
    isPricingGuidanceCapVariant,
    isPdpRespPreviewPage,
    isMobileFilterMenuUxVariant,
    isBannerAdsAbTestVariant,
    isShipsFromSBTileVariant,
}) => {
    return (
        <ServerVarsContext.Provider
            value={{
                isDesktop,
                isMobile,
                isSbAdsEnabled,
                isTablet,
                clientConf,
                localeOrigin,
                originalUrl,
                isSearchQueryApplied,
                locale,
                isSoldPDPV3Variant,
                isBot,
                isTradeQuickFiltersVariant,
                isPdpMwLayoutShiftFixVariant,
                isItemLocationFilterVariant,
                isTableSizeFilterVariant,
                urgencySignalsMwVariant,
                isContentVisibilitySbVariant,
                isPricingGuidanceCapVariant,
                isPdpRespPreviewPage,
                isMobileFilterMenuUxVariant,
                isBannerAdsAbTestVariant,
                isShipsFromSBTileVariant,
            }}
        >
            {children}
        </ServerVarsContext.Provider>
    );
};
